import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { PageShell } from "@/components/PageShell";
import { api, formatPostDate, youtubeEmbed } from "@/lib/api";

export default function PostPage() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const { data: post, isLoading, error } = useQuery({ queryKey: ["post", slug], queryFn: () => api(`/api/posts/${slug}`), retry: false });
  const embed = youtubeEmbed(post?.youtube_url);

  return (
    <PageShell testId="post-page" narrow>
      <Link to="/blog" data-testid="post-back-link" className="group inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-ember transition-colors hover:text-emberHover">
        <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" />
        {t.blog.back}
      </Link>

      {isLoading && <p className="mt-10 text-sm text-ash">{t.blog.loading}</p>}
      {error && <p className="mt-10 text-sm text-ash" data-testid="post-not-found">{t.blog.notFound}</p>}

      {post && (
        <article className="mt-10">
          <p className="flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.25em] text-ember" data-testid="post-meta">
            {t.blog.categories[post.category]}
            <span className="h-1 w-1 rotate-45 bg-ember/60" aria-hidden="true" />
            <span className="text-ash">{formatPostDate(post.created_at, lang)}</span>
          </p>
          <h1 className="mt-5 font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-cream sm:text-5xl" data-testid="post-title">
            {post.title}
          </h1>
          {post.cover_image && (
            <img src={post.cover_image} alt="" className="mt-10 aspect-[16/9] w-full rounded-md border border-line object-cover" data-testid="post-cover" />
          )}
          <div className="prose-editorial mt-10" data-testid="post-content" dangerouslySetInnerHTML={{ __html: post.content_html }} />
          {embed && (
            <div className="mt-12">
              <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-ember">{t.blog.watchVideo}</p>
              <div className="mt-4 aspect-video overflow-hidden rounded-md border border-line bg-black">
                <iframe
                  src={embed}
                  title={post.title}
                  loading="lazy"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="h-full w-full"
                  data-testid="post-video"
                />
              </div>
            </div>
          )}
        </article>
      )}
    </PageShell>
  );
}
